/**
 * Reward engine — enforcement for the tiered free-tattoo system.
 *
 * Recomputes on EVERY cart change (debounced, serialized by a busy flag,
 * always from a fresh /cart.js — never from stale DOM):
 *
 * - Downgrade: if the cart holds more _free_tattoo lines than the qualifying
 *   total has earned, only the OVERAGE is removed (newest lines first) in one
 *   /cart/update.js call. The picker is NOT opened — the customer stays in
 *   the cart drawer with the free lines they are still entitled to.
 * - Auto-restore: when the qualifying total crosses a tier upward again,
 *   previously picked tattoos (persisted in the _reward_picks cart
 *   attribute) are silently re-added up to the new allowance.
 * - Manual removal of a free line while still entitled just syncs
 *   _reward_picks — the engine never fights the customer.
 * - Restore-original: re-adds the paid line whose removal caused the
 *   downgrade, plus the previous picks.
 * - Threshold floor: writes the `_reward_min_total` (minor units) and
 *   `_reward_currency` cart attributes — the lowest qualifying total that
 *   still earns the free lines the cart holds, and the currency it is in.
 *   Carried into checkout so the custom app can block an order whose total
 *   fell under it (e.g. a 50%-off code applied at checkout, which the
 *   storefront never sees). Both cleared when no free lines remain.
 *
 * Tier data comes from the reward bar's data attributes (server-rendered by
 * snippets/free-shipping-bar.liquid from the treshold_config metaobject).
 * No tier data or bundle switched off -> the engine idles; it never guesses.
 *
 * SECURITY NOTE — best effort only. This runs client-side: a crafted POST,
 * disabled JS or a checkout permalink can still reach checkout with
 * unearned free items. True enforcement is the custom app's job
 * (order-webhook validation or a cart/checkout validation Function —
 * Functions are available to apps on non-Plus plans).
 */
(() => {
  'use strict';

  const DEBOUNCE_MS = 250;
  const PICKS_KEY = 'tattup:rewardPicks';
  const MIN_TOTAL_ATTR = '_reward_min_total';
  const CURRENCY_ATTR = '_reward_currency';
  const QUALIFYING_KEY = 'tattup:lastQualifying';
  const SNAPSHOT_KEY = 'tattup:cartSnapshot';
  const REMOVED_PAID_KEY = 'tattup:lastRemovedPaid';
  const CHECKOUT_SELECTOR = 'a[href*="/checkout"], [name="checkout"]';

  let busy = false;
  let debounceTimer = null;
  /** Last values computed by check() — fresh /cart.js truth, unlike the
   *  server-rendered bar, which only catches up when its region re-renders. */
  let lastState = { allowed: 0, freeQty: 0 };

  const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const rewardBar = () => document.querySelector('[data-reward-enabled]');

  /** Parsed "threshold:count" tier ladder from the bar, ascending. */
  function getTiers() {
    const bar = rewardBar();
    if (!bar || bar.dataset.rewardBundle !== '1' || !bar.dataset.rewardTiers) return null;
    const tiers = bar.dataset.rewardTiers
      .split(',')
      .map((entry) => {
        const [threshold, count] = entry.split(':');
        return { threshold: parseInt(threshold, 10), count: parseInt(count, 10) };
      })
      .filter((tier) => tier.threshold > 0);
    return tiers.length > 0 ? tiers : null;
  }

  /**
   * A reward line is one the picker granted (_free_tattoo property) OR any
   * line whose VARIANT price is 0 — free products a customer added by hand
   * from a direct URL must be governed too.
   *
   * `original_price` is the unit price BEFORE line-level discounts. Never use
   * final_price/final_line_price here: a 100%-off discount code zeroes those
   * while the variant still costs money, and such a line would then be
   * deleted by the engine and dropped from `qualifying`.
   */
  const isFreeLine = (item) => Boolean(item.properties && item.properties._free_tattoo)
    || item.original_price === 0;

  const freeLinesOf = (cart) => cart.items.filter(isFreeLine);

  /**
   * Eligibility runs on the cart TOTAL (post-discount), never on the sum of
   * the line prices: `final_line_price` carries line-level discounts only, so
   * an order-level discount code would leave this engine a tier above the
   * server-rendered bar. Mirrors `rw_qualifying` in
   * snippets/reward-tiers-data.liquid — cart.total_price minus the reward
   * lines (normally 0; the safety net for a mispriced free product).
   */
  // lines without shipping (KI credit packs, 28.10.2026) never count towards a tier either
  const qualifyingTotal = (cart) => Math.max(0, cart.items.reduce(
    (sum, item) => ((isFreeLine(item) || item.requires_shipping === false) ? sum - item.final_line_price : sum),
    cart.total_price
  ));

  const allowedFor = (tiers, qualifying) => tiers.reduce(
    (allowed, tier) => (qualifying >= tier.threshold ? tier.count : allowed),
    0
  );

  /**
   * The floor the qualifying total must not fall below for the free lines
   * currently in the cart to stay earned: the LOWEST tier threshold whose
   * count still covers `freeQty`. Written to the cart as the
   * `_reward_min_total` attribute (minor units) so the custom app can block
   * checkout when a discount code drops the total under it — the storefront
   * cannot see checkout-stage discounts.
   *
   * Requirement-based, not tier-based on purpose: a customer entitled to 4
   * who picked only 2 keeps those 2 down to the tier-2 threshold.
   * 0 free lines -> 0 (nothing to protect).
   */
  function minTotalFor(tiers, freeQty) {
    if (!tiers || freeQty <= 0) return 0;
    const tier = tiers.find((entry) => entry.count >= freeQty);
    return tier ? tier.threshold : tiers[tiers.length - 1].threshold;
  }

  /**
   * The floor plus the currency it is denominated in. A bare amount is
   * ambiguous the moment the customer switches currency: the thresholds are
   * re-rendered per market/currency, so a stored 3900 could be CHF or EUR.
   * The app must compare the checkout total against the floor ONLY when the
   * currencies match, and treat a mismatch as stale.
   *
   * `cart.currency` (presentment, from /cart.js) is the source — it is the
   * currency `cart.total_price` and therefore the tier thresholds are read in.
   * Empty strings remove both attributes (Shopify's clear semantics) so a
   * cart with no free lines carries nothing into checkout.
   */
  function minTotalAttributes(tiers, freeQty, currency) {
    const minTotal = minTotalFor(tiers, freeQty);
    return {
      [MIN_TOTAL_ATTR]: minTotal > 0 ? String(minTotal) : '',
      [CURRENCY_ATTR]: minTotal > 0 ? String(currency || '') : ''
    };
  }

  /**
   * Persist the floor, but only when it actually changed — every cart event
   * runs through here and an unconditional POST would double the requests.
   * A currency switch alone counts as a change: the amount is re-derived from
   * the freshly rendered tiers, so the pair never goes stale.
   */
  async function syncMinTotal(root, cart, tiers, freeQty) {
    const attributes = minTotalAttributes(tiers, freeQty, cart.currency);
    const unchanged = Object.keys(attributes).every(
      (key) => String((cart.attributes && cart.attributes[key]) || '') === attributes[key]
    );
    if (unchanged) return;
    await postJson(`${root}cart/update.js`, { attributes });
  }

  function readJson(storage, key, fallback) {
    try {
      const raw = storage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJson(storage, key, value) {
    try {
      if (value === null) storage.removeItem(key);
      else storage.setItem(key, JSON.stringify(value));
    } catch (e) { /* storage unavailable — degrade gracefully */ }
  }

  function picksFromCart(cart) {
    try {
      const raw = cart.attributes && cart.attributes._reward_picks;
      const picks = raw ? JSON.parse(raw) : [];
      // Tolerate bare ids as well as {id, handle} objects.
      return picks.map((pick) => (typeof pick === 'object' ? pick : { id: pick, handle: '' }));
    } catch (e) {
      return [];
    }
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`${url} ${response.status}`);
    return response.json();
  }

  const notifyCartChanged = () => {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  };

  /** Paid lines that shrank vs the previous snapshot (restore-original data). */
  function diffRemovedPaid(prevSnapshot, cart) {
    if (!Array.isArray(prevSnapshot)) return [];
    const currentByVariant = {};
    cart.items.forEach((item) => {
      if (!isFreeLine(item)) {
        currentByVariant[item.variant_id] = (currentByVariant[item.variant_id] || 0) + item.quantity;
      }
    });
    return prevSnapshot
      .filter((line) => !line.free)
      .map((line) => ({ id: line.id, quantity: line.qty - (currentByVariant[line.id] || 0) }))
      .filter((line) => line.quantity > 0);
  }

  const snapshotOf = (cart) => cart.items.map((item) => ({
    id: item.variant_id,
    qty: item.quantity,
    free: isFreeLine(item)
  }));

  function updatePdpButtons(allowed, freeQty) {
    document.querySelectorAll('[data-reward-pdp-btn]').forEach((btn) => {
      btn.hidden = allowed <= 0;
      const label = freeQty > 0 ? btn.dataset.labelSwap : btn.dataset.labelChoose;
      if (label) btn.textContent = label;
      // Attention state while tattoos are earned but the feature is untouched
      // — the in-bar button gets this from data-reward-freeqty, this one has
      // no bar ancestor. Keyed off freeQty like the label above, so the pulse
      // and the "Swap tattoos" wording can never disagree.
      btn.classList.toggle('is-unused', allowed > 0 && freeQty === 0);
    });
  }

  /** The core recompute-and-enforce pass. */
  async function check() {
    if (busy) return;
    const tiers = getTiers();
    if (!tiers) return; // no tier data for this market — never guess

    busy = true;
    try {
      const root = shopRoot();
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();

      const freeLines = freeLinesOf(cart);
      const freeQty = freeLines.reduce((sum, item) => sum + item.quantity, 0);
      const qualifying = qualifyingTotal(cart);
      const allowed = allowedFor(tiers, qualifying);
      const lastQualifying = readJson(window.sessionStorage, QUALIFYING_KEY, null);
      const prevSnapshot = readJson(window.sessionStorage, SNAPSHOT_KEY, null);
      const picks = picksFromCart(cart);

      // Mirror picks for the picker's pre-selection.
      writeJson(window.sessionStorage, PICKS_KEY, picks);

      // Post-enforcement free-line count — what the cart will actually hold
      // once this pass finishes, which is what `_reward_min_total` must guard.
      let effectiveFreeQty = freeQty;

      if (freeQty > allowed) {
        // DOWNGRADE — remove only the overage, newest free lines first
        // (/cart.js lists most-recently-added items first, so the earliest
        // picks survive). No picker: the customer stays in the cart drawer
        // with the free lines they still earn. `_reward_picks` is left
        // intact on purpose — crossing the tier upward again auto-restores
        // the removed pick(s).
        const removedPaid = diffRemovedPaid(prevSnapshot, cart);
        if (removedPaid.length > 0) writeJson(window.localStorage, REMOVED_PAID_KEY, removedPaid);

        const updates = {};
        let overage = freeQty - allowed;
        for (const item of freeLines) {
          if (overage <= 0) break;
          const removeQty = Math.min(item.quantity, overage);
          updates[item.key] = item.quantity - removeQty;
          overage -= removeQty;
        }
        await postJson(`${root}cart/update.js`, { updates });
        effectiveFreeQty = allowed;
        notifyCartChanged();
      } else if (freeQty < allowed && picks.length > freeQty) {
        // Possible UPGRADE — only auto-return picks when a tier was crossed
        // upward since the last pass; a manual free-line removal doesn't move
        // `qualifying`, so the engine won't fight the customer.
        const crossedUp = lastQualifying !== null
          && tiers.some((tier) => lastQualifying < tier.threshold && qualifying >= tier.threshold);

        if (crossedUp) {
          const inCart = new Set(freeLines.map((item) => String(item.variant_id)));
          const additions = picks
            .filter((pick) => !inCart.has(String(pick.id)))
            .slice(0, allowed - freeQty)
            .map((pick) => ({
              id: pick.id,
              quantity: 1,
              properties: { _free_tattoo: '1' }
            }));
          if (additions.length > 0) {
            await postJson(`${root}cart/add.js`, { items: additions });
            effectiveFreeQty = freeQty + additions.length;
            notifyCartChanged();
          }
        } else if (lastQualifying !== null) {
          // Customer manually removed a free tattoo while still entitled —
          // respect it: shrink the persisted picks to what's in the cart.
          const inCart = new Set(freeLines.map((item) => String(item.variant_id)));
          const keptPicks = picks.filter((pick) => inCart.has(String(pick.id)));
          if (keptPicks.length !== picks.length) {
            await postJson(`${root}cart/update.js`, {
              attributes: { _reward_picks: JSON.stringify(keptPicks) }
            });
            writeJson(window.sessionStorage, PICKS_KEY, keptPicks);
          }
        }
      }

      await syncMinTotal(root, cart, tiers, effectiveFreeQty);

      writeJson(window.sessionStorage, QUALIFYING_KEY, qualifying);
      writeJson(window.sessionStorage, SNAPSHOT_KEY, snapshotOf(cart));
      lastState = { allowed, freeQty: effectiveFreeQty };
      updatePdpButtons(allowed, freeQty);
    } catch (error) {
      // Leave the cart untouched on any failure; next cart event retries.
    } finally {
      busy = false;
    }
  }

  function scheduleCheck() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      check();
    }, DEBOUNCE_MS);
  }

  /**
   * Restore-original: bring back the removed paid line(s) + previous picks.
   *
   * Two separate /cart/add.js calls on purpose — that endpoint is
   * all-or-nothing, so a single dead pick id would otherwise also stop the
   * paid product from coming back. Picks already in the cart are skipped:
   * re-adding one would merge into qty 2, over-entitle the cart and trip the
   * downgrade pass into a reselect loop.
   */
  async function restoreOriginal() {
    if (busy) return;
    busy = true;
    try {
      const root = shopRoot();
      const removedPaid = readJson(window.localStorage, REMOVED_PAID_KEY, []);
      const picks = readJson(window.sessionStorage, PICKS_KEY, []);
      let changed = false;

      if (removedPaid.length > 0) {
        await postJson(`${root}cart/add.js`, {
          items: removedPaid.map((line) => ({ id: line.id, quantity: line.quantity }))
        });
        writeJson(window.localStorage, REMOVED_PAID_KEY, null);
        changed = true;
      }

      if (picks.length > 0) {
        const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
        const inCart = new Set(cart.items.map((item) => String(item.variant_id)));
        const items = picks
          .filter((pick) => !inCart.has(String(pick.id)))
          .map((pick) => ({ id: pick.id, quantity: 1, properties: { _free_tattoo: '1' } }));
        if (items.length > 0) {
          await postJson(`${root}cart/add.js`, { items });
          changed = true;
        }
      }

      if (changed) notifyCartChanged();
    } catch (error) {
      // Next cart event re-validates whatever state we ended up in.
    } finally {
      busy = false;
      scheduleCheck();
    }
  }

  /**
   * The free tattoos this cart has earned but not picked, or 0 when there is
   * nothing to offer. Deliberately NOT remembered: as long as the cart earns
   * free tattoos and holds none, every checkout click reopens the picker —
   * dismissing it or reloading the page does not buy a way past it. One free
   * line in the cart (any quantity) ends the prompt for good.
   */
  function unclaimedAllowance() {
    if (!document.querySelector('free-tattoo-picker')) return 0;
    if (lastState.allowed <= 0 || lastState.freeQty > 0) return 0;
    return lastState.allowed;
  }

  function openClaimPicker(allowed, opener) {
    document.dispatchEvent(new CustomEvent('dispatch:reward-picker:claim', {
      bubbles: true,
      detail: { allowed, opener }
    }));
  }

  /**
   * Checkout-click guard, two jobs:
   *
   * - (removed 26.10.2026) Unclaimed free tattoos used to force the picker open on
   *   every checkout click; they are optional now (owner decision).
   * - Race with the debounce: if a check is pending or running, hold the
   *   navigation until the pass finishes so the customer can't check out
   *   mid-enforcement. Anchors only — a form submit has no href to resume,
   *   and the /cart page button never had this hold.
   *
   * (Server-side enforcement is the custom app's responsibility — see the
   * header note.)
   */
  function guardCheckout(evt) {
    const trigger = evt.target.closest && evt.target.closest(CHECKOUT_SELECTOR);
    if (!trigger) return;
    if (!getTiers()) return;

    // 26.10.2026 (owner): no forced free-tattoo picker before checkout any more – «wenn Leute nichts wählen, wählen sie
    // nichts». Unclaimed free tattoos stay optional (choose them in the cart drawer / sticky bar). Mobile analysis: the forced
    // picker (no «continue without», closing it closed the cart, came back on every tap) was the main cart→checkout leak.
    if (!trigger.href || (!debounceTimer && !busy)) return;

    evt.preventDefault();
    evt.stopPropagation();
    clearTimeout(debounceTimer);
    debounceTimer = null;
    const href = trigger.href;
    check().then(() => {
      // the delivery property of every line is refreshed first (assets/tt-eta.js)
      if (window.TTEta) window.TTEta.beforeCheckout().then(() => { window.location.href = href; });
      else window.location.href = href;
    });
  }

  /**
   * Shared surface for the picker: one canonical isFreeLine, plus a way to
   * hold enforcement while the picker performs its own cart mutations.
   */
  window.TattooRewards = {
    isFreeLine,
    /** Floor + currency attributes for `n` free lines, so the picker can
     *  write them in the same /cart/update.js call that persists the picks. */
    minTotalAttributes: (freeQty, currency) => minTotalAttributes(getTiers(), freeQty, currency),
    suspend() { busy = true; },
    resume() { busy = false; scheduleCheck(); }
  };

  document.addEventListener('on:cart:change', scheduleCheck);
  document.addEventListener('on:cart:after-merge', scheduleCheck);
  document.addEventListener('dispatch:reward:restore', restoreOriginal);
  document.addEventListener('click', guardCheckout, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => check());
  } else {
    check();
  }
})();
